import { useEffect, useRef } from 'react';
import type { AppSummary } from '../lib/api';
import '../styles/confirm.scss';

interface Props {
  app: AppSummary;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Interposed before a validated customer's instance is launched, for SafetyNet
 * staff only — a customer launching their own single instance never sees it.
 *
 * A native `<dialog>` rather than a hand-built overlay: `showModal` brings the
 * focus trap, the inert backdrop and Escape-to-close with it, none of which a
 * div gets for free.
 */
export function ValidatedLaunchDialog({ app, onCancel, onConfirm }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  // Mounted only while a launch is pending, so opening on mount is the whole
  // lifecycle. Escape fires `cancel` then `close`, which `onClose` covers.
  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog ref={ref} className="x5-confirm" onClose={onCancel} aria-labelledby="x5-confirm-title">
      <div className="x5-confirm__head">
        <span className="x5-confirm__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path
              d="M12 3.2 21 19H3z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinejoin="round"
            />
            <path d="M12 9.5v4.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            <circle cx="12" cy="16.4" r="1.05" fill="currentColor" />
          </svg>
        </span>
        <h2 className="x5-confirm__title" id="x5-confirm-title">
          Validated instance
        </h2>
      </div>

      <p className="x5-confirm__name">{app.displayName}</p>
      <p className="x5-confirm__body">
        This is a customer&rsquo;s validated system. Anything done in the session is done on their
        validated environment, so open it only if you mean to work in this one.
      </p>

      <div className="x5-confirm__actions">
        <button
          type="button"
          className="x5-confirm__button"
          onClick={() => ref.current?.close()}
          autoFocus
        >
          Cancel
        </button>
        <button
          type="button"
          className="x5-confirm__button x5-confirm__button--go"
          onClick={onConfirm}
        >
          Launch
        </button>
      </div>
    </dialog>
  );
}
